import {
  html,
  attr,
  prop,
  Signal,
  When,
  on,
  Ensure,
  computedOf,
} from '@tempots/dom'
import { Anchor, NavigationService } from '@tempots/ui'
import { Button, Icon, Modal, InputWrapper, TextInput } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { DataTable } from '@/components/data'
import { EmptyState, StatusBadge } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import { formatDate } from '@/utils/formatting'
import { apiKeys as apiKeysService } from '@/services/gateway'
import { Workspace } from '@/state/workspace'
import type { ApiKeyResponse } from '@/services/types'

// Permission options for each category (hierarchical - each level includes lower levels)
const FILES_PERMISSIONS = [
  { value: '', label: 'None', description: 'No file access' },
  { value: 'files:read', label: 'Read', description: 'Download files' },
  { value: 'files:write', label: 'Write', description: 'Upload files' },
  {
    value: 'files:own',
    label: 'Own',
    description: 'Upload + delete own files',
  },
  {
    value: 'files:all',
    label: 'All',
    description: 'Upload + delete any files',
  },
] as const

const JOBS_PERMISSIONS = [
  { value: '', label: 'None', description: 'No job access' },
  { value: 'jobs:read', label: 'Read', description: 'View job status' },
  { value: 'jobs:write', label: 'Write', description: 'Submit jobs' },
  { value: 'jobs:own', label: 'Own', description: 'Submit + cancel own jobs' },
  { value: 'jobs:all', label: 'All', description: 'Submit + cancel any jobs' },
] as const

function getApiKeyStatusVariant(status: string | undefined): BadgeVariant {
  return status === 'revoked' ? 'neutral' : 'success'
}

function getApiKeyStatusLabel(status: string | undefined): string {
  return status === 'revoked' ? 'Revoked' : 'Active'
}

export interface ApiKeysPageOptions {
  projectId: Signal<string>
}

export function ApiKeysPage({ projectId }: ApiKeysPageOptions) {
  projectId.on(Workspace.changeProject)

  const apiKeys = projectId.mapAsync(
    async (id) =>
      (id == null ? null : (await apiKeysService.list(id)).keys) ?? [],
    null
  )

  // Form state for create modal
  const newKeyName = prop('')
  const filesPermission = prop('') // Empty = none/default
  const jobsPermission = prop('') // Empty = none/default
  const isCreating = prop(false)
  const createError = prop<string | null>(null)

  // Get current project for display
  const currentProjectName = Workspace.project.$.name

  async function handleCreateApiKey(close: () => void) {
    const name = newKeyName.get().trim()
    if (!name) {
      createError.set('API key name is required')
      return
    }

    const pid = projectId.value
    if (!pid) {
      createError.set('No project selected')
      return
    }

    isCreating.set(true)
    createError.set(null)

    try {
      // Build scopes array from selected permissions
      const scopes: string[] = []
      const files = filesPermission.get()
      const jobs = jobsPermission.get()
      if (files) scopes.push(files)
      if (jobs) scopes.push(jobs)

      const newKey = await apiKeysService.create(pid, {
        name,
        scopes: scopes.length > 0 ? scopes : undefined,
      })

      // Reset form
      newKeyName.set('')
      filesPermission.set('')
      jobsPermission.set('')
      close()

      // Navigate to detail page with the full key in state
      // Store the key temporarily in sessionStorage so detail page can show it
      sessionStorage.setItem(`newApiKey_${newKey.id}`, newKey.key)
      NavigationService.navigate(
        Workspace.projectUrl(`/api-keys/${newKey.id}`, projectId.value) ?? ''
      )
    } catch (err) {
      createError.set(
        err instanceof Error ? err.message : 'Failed to create API key'
      )
    } finally {
      isCreating.set(false)
    }
  }

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-2'),
      'API Keys',
      When(
        currentProjectName.map((name) => name !== null),
        () =>
          html.span(
            attr.class('text-gray-400 text-lg font-normal'),
            currentProjectName.map((name) => ` - ${name}`)
          )
      )
    ),
    content: html.div(
      attr.class('space-y-6'),
      // Header with create button
      html.div(
        attr.class(
          'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'
        ),
        html.div(
          html.p(
            attr.class('text-sm text-gray-600'),
            'Manage API keys for this project. Each key is scoped to this project only.'
          )
        ),
        Modal({ size: 'md', dismissable: true }, (open, close) =>
          html.div(
            Button(
              {
                onClick: () => {
                  newKeyName.set('')
                  filesPermission.set('')
                  jobsPermission.set('')
                  createError.set(null)
                  open({
                    header: html.h2(
                      attr.class('text-lg font-semibold text-gray-900'),
                      'Create API Key'
                    ),
                    body: html.div(
                      attr.class('space-y-4'),
                      html.p(
                        attr.class('text-sm text-gray-600'),
                        'Create a new API key for this project.'
                      ),
                      InputWrapper({
                        label: 'Key Name',
                        required: true,
                        content: TextInput({
                          value: newKeyName,
                          placeholder: 'Production API Key',
                          onInput: (v) => newKeyName.set(v),
                        }),
                        hasError: createError.map((e) => e !== null),
                        error: createError,
                      }),
                      // Permissions radio buttons
                      html.div(
                        attr.class('space-y-4'),
                        html.label(
                          attr.class('block text-sm font-medium text-gray-700'),
                          'Permissions (optional)'
                        ),
                        html.p(
                          attr.class('text-xs text-gray-500 mb-2'),
                          'Leave as "None" for default permissions (files:own, jobs:own)'
                        ),
                        // Files permission group
                        html.div(
                          attr.class('space-y-2'),
                          html.p(
                            attr.class('text-sm font-medium text-gray-600'),
                            'Files'
                          ),
                          html.div(
                            attr.class('flex flex-wrap gap-2'),
                            ...FILES_PERMISSIONS.map((perm) =>
                              html.label(
                                attr.class(
                                  filesPermission.map((selected) =>
                                    selected === perm.value
                                      ? 'flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-brand-600 bg-brand-50 cursor-pointer'
                                      : 'flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer'
                                  )
                                ),
                                html.input(
                                  attr.type('radio'),
                                  attr.name('files-permission'),
                                  attr.class('sr-only'),
                                  attr.checked(
                                    filesPermission.map((v) => v === perm.value)
                                  ),
                                  on.change(() =>
                                    filesPermission.set(perm.value)
                                  )
                                ),
                                html.span(
                                  attr.class('text-sm font-medium'),
                                  perm.label
                                )
                              )
                            )
                          )
                        ),
                        // Jobs permission group
                        html.div(
                          attr.class('space-y-2'),
                          html.p(
                            attr.class('text-sm font-medium text-gray-600'),
                            'Jobs'
                          ),
                          html.div(
                            attr.class('flex flex-wrap gap-2'),
                            ...JOBS_PERMISSIONS.map((perm) =>
                              html.label(
                                attr.class(
                                  jobsPermission.map((selected) =>
                                    selected === perm.value
                                      ? 'flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-brand-600 bg-brand-50 cursor-pointer'
                                      : 'flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer'
                                  )
                                ),
                                html.input(
                                  attr.type('radio'),
                                  attr.name('jobs-permission'),
                                  attr.class('sr-only'),
                                  attr.checked(
                                    jobsPermission.map((v) => v === perm.value)
                                  ),
                                  on.change(() =>
                                    jobsPermission.set(perm.value)
                                  )
                                ),
                                html.span(
                                  attr.class('text-sm font-medium'),
                                  perm.label
                                )
                              )
                            )
                          )
                        )
                      )
                    ),
                    footer: html.div(
                      attr.class('flex justify-end gap-3'),
                      Button({ variant: 'outline', onClick: close }, 'Cancel'),
                      Button(
                        {
                          disabled: isCreating,
                          onClick: () => handleCreateApiKey(close),
                        },
                        isCreating.map((c) =>
                          c ? 'Creating...' : 'Create API Key'
                        )
                      )
                    ),
                  })
                },
              },
              Icon({ icon: 'mdi:plus' }, attr.class('w-4 h-4 mr-2')),
              'Create API Key'
            )
          )
        )
      ),
      // Table/Cards (only when loaded)
      Ensure(
        apiKeys,
        (keys) =>
          html.div(
            attr.class(
              'bg-white rounded-xl border border-gray-200 overflow-hidden'
            ),
            DataTable({
              columns: [
                {
                  key: 'name',
                  header: 'Name',
                  render: (item) =>
                    html.div(
                      html.p(
                        attr.class('font-medium text-gray-900'),
                        item.map((k) => k.name)
                      ),
                      When(
                        item.map((k) => (k.scopes?.length || 0) > 0),
                        () =>
                          html.p(
                            attr.class('text-sm text-gray-500'),
                            item.map((k) =>
                              k.scopes ? `Scopes: ${k.scopes.join(', ')}` : ''
                            )
                          )
                      )
                    ),
                },
                {
                  key: 'prefix',
                  header: 'Key Prefix',
                  render: (item) =>
                    html.code(
                      attr.class('text-sm bg-gray-100 px-2 py-1 rounded'),
                      item.map((k) => k.prefix)
                    ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (item) =>
                    StatusBadge({
                      label: item.map((k) => getApiKeyStatusLabel(k.status)),
                      variant: item.map((k) =>
                        getApiKeyStatusVariant(k.status)
                      ),
                    }),
                },
                {
                  key: 'createdAt',
                  header: 'Created',
                  render: (item) =>
                    html.span(
                      attr.class('text-gray-500 text-sm'),
                      item.map((k) => formatDate(k.createdAt))
                    ),
                },
                {
                  key: 'lastUsedAt',
                  header: 'Last Used',
                  render: (item) =>
                    html.span(
                      attr.class('text-gray-500 text-sm'),
                      item.map((k) =>
                        k.lastUsedAt ? formatDate(k.lastUsedAt) : 'Never'
                      )
                    ),
                },
                {
                  key: 'id',
                  header: '',
                  render: (item) =>
                    Anchor(
                      {
                        href: computedOf(
                          projectId,
                          item
                        )(
                          (pid, k) =>
                            Workspace.projectUrl(`/api-keys/${k.id}`, pid) ?? ''
                        ),
                      },
                      attr.class(
                        'text-brand-600 hover:text-brand-700 text-sm font-medium'
                      ),
                      'View'
                    ),
                },
              ],
              data: keys,
              renderMobileCard: (key) => ApiKeyCard(projectId, key),
              emptyMessage: 'No API keys yet. Create one to get started.',
            })
          ),
        () =>
          EmptyState({
            iconName: 'mdi:key',
            title: 'No API keys yet',
            description:
              'Create your first API key to start using the Verts API for this project.',
          })
      )
    ),
  })
}

function ApiKeyCard(projectId: Signal<string>, key: Signal<ApiKeyResponse>) {
  return html.div(
    attr.class('bg-white border border-gray-200 rounded-lg p-4'),
    html.div(
      attr.class('flex items-start justify-between'),
      html.div(
        html.div(
          attr.class('flex items-center gap-2'),
          html.h3(
            attr.class('font-medium text-gray-900'),
            key.map((k) => k.name)
          ),
          StatusBadge({
            label: key.map((k) => getApiKeyStatusLabel(k.status)),
            variant: key.map((k) => getApiKeyStatusVariant(k.status)),
          })
        ),
        When(
          key.map((k) => (k.scopes?.length || 0) > 0),
          () =>
            html.p(
              attr.class('text-sm text-gray-500 mt-1'),
              key.map((k) => (k.scopes ? `Scopes: ${k.scopes.join(', ')}` : ''))
            )
        )
      ),
      Anchor(
        {
          href: computedOf(
            projectId,
            key
          )((pid, k) => Workspace.projectUrl(`/api-keys/${k.id}`, pid) ?? '#'),
        },
        attr.class('text-brand-600 hover:text-brand-700'),
        Icon({ icon: 'mdi:chevron-right' }, attr.class('w-5 h-5'))
      )
    ),
    html.div(
      attr.class('mt-3'),
      html.code(
        attr.class('text-sm bg-gray-100 px-2 py-1 rounded'),
        key.map((k) => k.prefix)
      )
    ),
    html.div(
      attr.class('mt-3 flex items-center gap-4 text-xs text-gray-500'),
      html.span(
        'Created: ',
        key.map((k) => formatDate(k.createdAt))
      ),
      html.span(
        'Last used: ',
        key.map((k) => (k.lastUsedAt ? formatDate(k.lastUsedAt) : 'Never'))
      )
    )
  )
}
